import * as React from 'react';
import {
    DatePicker,
    Select,
    Input,
    Button,
} from 'antd';
import {
    ReloadOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { StylesProps } from '../../../../../theme/jss-types';
import { gridView1Styles } from './grid-view-1.styles';
import { DASHBOARD_STATUS_GROUPS } from '../constants';
import { FilterMetadata, Master } from '../../../../../types/master-data-types';
import ViewToggle from './view-toggle';

const { RangePicker } = DatePicker;

interface FilterBarProps extends StylesProps<ReturnType<typeof gridView1Styles>> {
    filters: Record<string, any>;
    onDateChange: (value: any) => void;
    onChangeFilter: (filter: string, value: any) => void;
    onRefresh: () => void;
    onDownload: () => void;
    downloading?: boolean;
    onSearchTypeChange: (value: string) => void;
    onSearchValueChange: (value: string) => void;
    onSearchInputSubmit: (searchType: string, searchValue: string) => void;
    selectedSearchType: string;
    inputFilterVal: string;
    filterMetadata: FilterMetadata[];
    viewMode: string | null | undefined;
    onViewModeChange: (mode: string | null) => void;
    viewIdFromConfig: string | null | undefined;
    master: Master;
    isMoreFilterAdded: () => boolean;
    handleResetFilters: () => void;
}

const FilterBar: React.FC<FilterBarProps> = (props) => {
    const {
        classes,
        filters,
        onDateChange,
        onChangeFilter,
        onRefresh,
        onDownload,
        downloading = false,
        onSearchTypeChange,
        onSearchValueChange,
        onSearchInputSubmit,
        selectedSearchType,
        inputFilterVal,
        filterMetadata,
        viewMode,
        onViewModeChange,
        viewIdFromConfig,
        master,
        handleResetFilters,
        isMoreFilterAdded,
    } = props;

    const { t } = useTranslation();

    // Derive selected status groups from filters.statusList (no separate state)
    const selectedStatusGroups = React.useMemo(() => {
        // Parse statusList filter (stored as string in reducer)
        if (!filters.statusList) {
            return ['All']; // Default to "All" when no status filter is applied
        }

        if (typeof filters.statusList === 'string') {
            const currentStatusArray = filters.statusList.split(/[,\s]+/).filter(Boolean);

            if (currentStatusArray.length === 0) {
                return ['All'];
            }

            // Determine which status groups contain the filtered statuses
            const matchingGroups: string[] = [];
            Object.keys(DASHBOARD_STATUS_GROUPS).forEach((groupName) => {
                if (groupName === 'All') return; // Skip "All" group

                const groupStatuses = (DASHBOARD_STATUS_GROUPS as any)[groupName] || [];
                // Check if any status from the filter matches this group
                const hasMatchingStatus = currentStatusArray.some((status) => groupStatuses.includes(status));

                if (hasMatchingStatus) {
                    matchingGroups.push(groupName);
                }
            });

            return matchingGroups.length > 0 ? matchingGroups : ['All'];
        }

        return ['All'];
    }, [filters.statusList]);

    // Get filter metadata for specific filters
    const filterMetadataMap = React.useMemo(() => {
        return filterMetadata?.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {} as Record<string, FilterMetadata>) || {};
    }, [filterMetadata]);

    const getFilterMetadata = React.useCallback((filterId: string) => {
        return filterMetadataMap[filterId];
    }, [filterMetadataMap]);

    const originNameFilter = React.useMemo(() => getFilterMetadata('originNameList'), [filterMetadataMap]);
    const originCountryFilter = React.useMemo(() => getFilterMetadata('originCountryList'), [filterMetadataMap]);
    const destinationNameFilter = React.useMemo(() => getFilterMetadata('destinationNameList'), [filterMetadataMap]);
    const destinationCountryFilter = React.useMemo(() => getFilterMetadata('destinationCountryList'), [filterMetadataMap]);
    const courierPartnerFilter = React.useMemo(() => getFilterMetadata('courierPartnerList'), [filterMetadataMap]);

    const courierPartnerOptions = React.useMemo(() => {
        const opts = courierPartnerFilter?.options || [];
        if (opts.length === 0) return [];
        return [
            ...opts.map((option: any) => ({ label: option.label, value: option.value })),
            { label: 'SELF', value: 'SELF' },
        ];
    }, [courierPartnerFilter]);

    const defaultInputState = React.useMemo(() => ({
        type: null as string | null,
        value: '',
    }), []);
    const [inputFilterState, setInputFilterState] = React.useState<{ type: string | null; value: string }>(
        defaultInputState,
    );

    const resetInputState = React.useCallback(() => {
        setInputFilterState(defaultInputState);
    }, [defaultInputState]);

    const getInputValue = (filterKey: string) => {
        if (inputFilterState.type === filterKey) {
            return inputFilterState.value;
        }
        return filters[filterKey] || '';
    };

    const handleInputFocus = (filterKey: string) => {
        const currentValue = filters[filterKey] || '';
        setInputFilterState({ type: filterKey, value: currentValue });
    };

    const handleInputChange = (filterKey: string, value: string) => {
        setInputFilterState({ type: filterKey, value });
    };

    const handleInputApply = (filterKey: string) => {
        const activeValue = inputFilterState.type === filterKey ? inputFilterState.value : filters[filterKey] || '';
        const trimmedValue = activeValue?.trim();
        onChangeFilter(filterKey, trimmedValue ? [trimmedValue] : undefined);
        resetInputState();
    };

    // Get customer list for Protocol ID (same as customer code filter)
    const childList = master?.children_list || [];
    const customerOptions = React.useMemo(() => {
        return childList.map((child: any) => ({
            label: `${child.customer_code} (${child.customer_name})`,
            value: child.customer_id,
        }));
    }, [childList]);

    // Handle Quick Filter selection (multi-select, additive)
    const handleQuickFilterClick = (groupName: string) => {
        const statusList = (DASHBOARD_STATUS_GROUPS as any)[groupName] || [];

        // Parse existing statusList (can be string or array format)
        let currentStatusArray: string[] = [];
        if (Array.isArray(filters.statusList)) {
            currentStatusArray = [...filters.statusList];
        } else if (typeof filters.statusList === 'string' && filters.statusList) {
            currentStatusArray = filters.statusList.split(/[,\s]+/).filter(Boolean);
        }

        // Special handling for "All" filter
        if (groupName === 'All') {
            if (selectedStatusGroups.includes('All')) {
                // Deselect "All" - do nothing, keep current filters
                // Keep existing statusList as is
                return;
            }
            // Select "All" - clear all status filters
            onChangeFilter('statusList', undefined);
            return;
        }

        // For other filters: deselect "All" if it's selected
        let newStatusArray: string[] = [...currentStatusArray];

        if (selectedStatusGroups.includes(groupName)) {
            // Deselect this group - remove its statuses from filter
            newStatusArray = newStatusArray.filter((status) => !statusList.includes(status));
        } else {
            // Select this group - add its statuses to filter
            // Add new statuses (avoid duplicates)
            statusList.forEach((status: string) => {
                if (!newStatusArray.includes(status)) {
                    newStatusArray.push(status);
                }
            });
        }

        // Convert array back to comma-separated string (existing format)
        const statusListString = newStatusArray.length > 0 ? newStatusArray.join(',') : undefined;
        onChangeFilter('statusList', statusListString);
    };

    // Get tag class name for quick filter
    const getQuickFilterClassName = (groupName: string) => {
        const isSelected = selectedStatusGroups.includes(groupName);
        const baseClasses = [classes.filterTagBase];

        switch (groupName) {
            case 'In Transit':
                baseClasses.push(isSelected ? classes.filterTagInTransitActive : classes.filterTagInTransit);
                break;
            case 'Delivered':
                baseClasses.push(isSelected ? classes.filterTagDeliveredActive : classes.filterTagDelivered);
                break;
            case 'In Process':
                baseClasses.push(isSelected ? classes.filterTagInProcessActive : classes.filterTagInProcess);
                break;
            case 'Cancelled':
                baseClasses.push(isSelected ? classes.filterTagCancelledActive : classes.filterTagCancelled);
                break;
            case 'All':
                baseClasses.push(classes.filterTagAll);
                break;
            default:
                break;
        }

        return baseClasses.join(' ');
    };

    const disabledDate = (current: any) => {
        return current && current > moment().endOf('day');
    };

    // Search type options
    const searchTypeOptions = React.useMemo(() => [
        { label: t('consignment_number'), value: 'referenceNumberList' },
        { label: t('customer_reference_number'), value: 'customerReferenceNumberList' },
    ], [t]);

    const renderResetFiltersOption = () => {
        const filterApplied = isMoreFilterAdded();
        if (!filterApplied) {
            return null;
        }
        return (
            <div
                className={classes.resetFilter}
                onClick={() => handleResetFilters()}
            >
                {t('reset_filters')}
            </div>
        );
    };

    return (
        <div className={classes.filterBarContainer}>
            {/* Top Filter Row */}
            <div className={classes.topFilterRow}>
                {/* Date Range Picker */}
                <RangePicker
                    className={classes.dateRangePicker}
                    value={[moment(filters.startDate), moment(filters.endDate)]}
                    allowClear={false}
                    allowEmpty={[false, false]}
                    disabledDate={disabledDate}
                    placeholder={['Select Start Date', 'Select End Date']}
                    onChange={onDateChange}
                />

                {/* Protocol ID Dropdown (same as customer code filter) */}
                <Select
                    className={classes.dropdownSelect}
                    value={filters.customerIdList}
                    allowClear
                    showSearch
                    placeholder={t('protocol_id')}
                    onChange={(value) => onChangeFilter('customerIdList', value)}
                    options={customerOptions}
                    optionFilterProp="label"
                    dropdownMatchSelectWidth={false}
                />

                {/* Connected Input Group: Search Type + Search Input */}
                <div className={classes.connectedInputGroup}>
                    <Select
                        className={classes.inputGroupSelect}
                        value={selectedSearchType}
                        onChange={(value) => {
                            onSearchTypeChange(value);
                            if (inputFilterVal && inputFilterVal.trim()) {
                                onSearchInputSubmit(value, inputFilterVal);
                            }
                        }}
                        options={searchTypeOptions}
                    />
                    <Input
                        className={classes.inputGroupInput}
                        value={inputFilterVal}
                        placeholder={t(`${selectedSearchType}_placeholder`)}
                        onChange={(e) => onSearchValueChange(e.target.value)}
                        onPressEnter={() => {
                            // Apply filter with selected search type and input value (clears other filter type)
                            onSearchInputSubmit(selectedSearchType, inputFilterVal);
                        }}
                    />
                </div>

                {/* Action Buttons Group */}
                <div className={classes.actionButtonsGroup}>
                    {/* Refresh Button */}
                    <Button
                        className={classes.iconButton}
                        onClick={onRefresh}
                        icon={<ReloadOutlined />}
                    />

                    {/* Download Button - Hidden in lane view */}
                    {viewMode !== 'lane_view_1' && (
                        <Button
                            className={classes.iconButton}
                            onClick={onDownload}
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            disabled={downloading}
                        />
                    )}

                    {/* View Toggle Component */}
                    <ViewToggle
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        viewIdFromConfig={viewIdFromConfig}
                    />
                </div>
            </div>

            {/* Bottom Filter Row */}
            <div className={classes.bottomFilterRow}>
                {/* Quick Filters Section - Hidden in lane view */}
                {viewMode !== 'lane_view_1' && (
                    <div className={classes.quickFiltersSection}>
                        <span className={classes.filterSectionLabel}>
                            {`${t('quick_filters')}:`}
                        </span>

                        {/* Quick Filter Tags */}
                        {Object.keys(DASHBOARD_STATUS_GROUPS).map((groupName) => (
                            <div
                                key={groupName}
                                className={getQuickFilterClassName(groupName)}
                                onClick={() => handleQuickFilterClick(groupName)}
                            >
                                {t(groupName)}
                            </div>
                        ))}

                        {renderResetFiltersOption()}

                    </div>
                )}

                {/* More Filters Section - Always visible */}
                <div className={classes.moreFiltersSection}>
                    <span className={classes.filterSectionLabel}>
                        {`${t('more_filters')}:`}
                    </span>

                    {[
                        {
                            filter: originNameFilter,
                            key: 'originNameList',
                        },
                        {
                            filter: originCountryFilter,
                            key: 'originCountryList',
                        },
                        {
                            filter: destinationNameFilter,
                            key: 'destinationNameList',
                        },
                        {
                            filter: destinationCountryFilter,
                            key: 'destinationCountryList',
                        },
                    ].map(({ filter, key }) => filter && (
                        <Input
                            key={key}
                            id={key}
                            className={classes.input}
                            value={getInputValue(key)}
                            placeholder={t(key)}
                            onFocus={() => handleInputFocus(key)}
                            onChange={(e) => handleInputChange(key, e.target.value)}
                            onPressEnter={() => handleInputApply(key)}
                            onBlur={resetInputState}
                        />
                    ))}

                    {courierPartnerFilter && (
                        <Select
                            className={classes.moreFiltersDropdownSelect}
                            value={Array.isArray(filters.courierPartnerList)
                                ? filters.courierPartnerList[0]
                                : filters.courierPartnerList}
                            allowClear
                            showSearch
                            placeholder={t('courierPartnerList')}
                            onChange={(value) => onChangeFilter('courierPartnerList', value ? [value] : undefined)}
                            options={courierPartnerOptions}
                            optionFilterProp="label"
                        />
                    )}
                    {viewMode === 'lane_view_1' && renderResetFiltersOption()}
                </div>
            </div>
        </div>
    );
};

export default FilterBar;
